import axios from 'axios';
import to from 'await-to-js';

const getInitialState = () => ({
  validations: [],
  loading: false,
});

const store = {
  namespaced: true,

  state: getInitialState(),

  getters: {
    /**
     * Returns a Map of productKey -> array of validation failure docs
     * for O(1) lookup by the grid cell renderer and row class logic.
     */
    validationsByProductKey: state => {
      const map = new Map();
      state.validations.forEach(v => {
        const key = v.productKey;
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key).push(v);
      });
      return map;
    },
  },

  mutations: {
    resetState(state) {
      Object.assign(state, getInitialState());
    },

    setValidations(state, validations) {
      state.validations = validations;
    },

    /**
     * Merges validation results for specific products into the existing state.
     * Replaces all existing validations for the given productKeys with the new
     * ones, while preserving validations for all other products.
     *
     * @param {Object} state - Vuex state
     * @param {Object} payload
     * @param {number[]} payload.productKeys - Product keys whose validations are being updated
     * @param {Object[]} payload.validations - New validation docs for those products
     */
    mergeProductValidations(state, { productKeys, validations }) {
      const keysSet = new Set(productKeys);
      // Keep validations for products not in the update set
      const retained = state.validations.filter(v => !keysSet.has(v.productKey));
      // Replace state with retained + new validations (triggers getter recomputation)
      state.validations = [...retained, ...validations];
    },

    setLoading(state, loading) {
      state.loading = loading;
    },
  },

  actions: {
    async fetchValidations({ commit }, { workpackageId, scenarioKey }) {
      commit('setLoading', true);
      const params = {};
      if (scenarioKey) {
        params.scenarioKey = scenarioKey;
      }
      const [err, response] = await to(
        axios.get(`/api/price-validations/workpackage/${workpackageId}`, { params })
      );
      if (err) {
        commit('setValidations', []);
      } else {
        commit('setValidations', response.data);
      }
      commit('setLoading', false);
    },

    /**
     * Fetches validations for a single product and merges them into state.
     * Used by the legacy override path which doesn't return inline validation data.
     */
    async fetchProductValidations({ commit }, { workpackageId, productKey, scenarioKey }) {
      const params = {};
      if (scenarioKey) {
        params.scenarioKey = scenarioKey;
      }
      const [err, response] = await to(
        axios.get(`/api/price-validations/workpackage/${workpackageId}/product/${productKey}`, {
          params,
        })
      );
      const validations = err ? [] : response.data;
      commit('mergeProductValidations', { productKeys: [productKey], validations });
    },

    resetState({ commit }) {
      commit('resetState');
    },
  },
};

export default store;
